import { TalentLanguage } from './cv-generator.types';
import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { Style } from '@react-pdf/types';
import isEmpty from 'lodash/isEmpty';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import DateUtils from '@utils/date.utils';

import { EmailIcon, InstagramIcon, LinkedInIcon, LocationIcon, PhoneNumberIcon, PoweredByUpercut, UpercutIconGrey } from './CvTemplateIcons';
import { Certificate, Diploma, Experience, ITalent } from './cv-generator.types';
import { startCase } from 'lodash';

const { getYear } = DateUtils;

Font.register({ family: 'Inter', src: '/fonts/Inter.ttf' });
Font.register({ family: 'Inter600', src: '/fonts/Inter-SemiBold.ttf' });
Font.register({ family: 'Inter700', src: '/fonts/Inter-Bold.ttf' });
Font.registerHyphenationCallback(word => [word]);

const WrapText = ({ text, style }: { text?: string; style?: Style }) => (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', ...style }}>
        {text?.match(/\w+|\W+/g)?.map((seg, i) => (
            <Text key={i}>{seg}</Text>
        ))}
    </View>
);

const CvTemplateClassic = ({ userData, hardSkills }: { userData: ITalent; hardSkills: string[] }) => {
    const {
        fullName,
        city,
        logo,
        email,
        phoneNumber: rawNumbers,
        social,
        bio,
        diplomas,
        softSkills,
        experiences,
        certificates,
        publicProfile,
        talentLanguages,
    } = userData;
    const phoneNumber = rawNumbers.startsWith('+')
        ? formatPhoneNumberIntl(rawNumbers)
        : formatPhoneNumberIntl(`+33 ${rawNumbers}`);
    const getYearRange = (startDate: string, endDate: string) =>
        `${getYear(startDate)} - ${endDate ? getYear(endDate) : 'present'}`;
    const details = [
        { text: email, icon: EmailIcon },
        { text: phoneNumber, icon: PhoneNumberIcon },
        { text: publicProfile, icon: UpercutIconGrey },
        { text: social?.linkedin, icon: LinkedInIcon },
        { text: social?.instagram, icon: InstagramIcon },
    ];

    const renderLeftSectionHeader = (title: string, width: string) => (
        <View wrap={false}>
            <Text style={styles.sectionTitle}>{title}</Text>
            <View style={[styles.leftSectionRect, { width }]} />
        </View>
    );

    const renderRightSectionHeader = (title: string, width: string) => (
        <View wrap={false}>
            <Text style={[styles.sectionTitle, { marginLeft: '24px' }]}>{title}</Text>
            <View style={[styles.rightSectionRect, { marginLeft: '24px', width }]} />
        </View>
    );

    const renderNameSection = () => (
        <View style={styles.nameSection}>
            <Text style={styles.name}>{fullName}</Text>
            <View style={{ flexDirection: 'row', marginTop: '8px', alignItems: 'center' }}>
                <LocationIcon style={{ marginRight: '8px' }} />
                <Text style={[styles.regularText, { width: '133px' }]}>
                    {/* TODO: hardcoded country */}
                    {`${city}, France`}
                </Text>
            </View>
        </View>
    );

    // TODO: change if there is a better way
    // currently made this way due to react-pdf/renderer's
    // hyphenationCallback prop causing things to break
    const getStringsWithLimit = (text: string, cutoffLength: number) => {
        let detailStrings = [text];
        if (text.length > cutoffLength) {
            detailStrings = [];
            for (let i = 0; i < Math.ceil(text.length / cutoffLength); i++) {
                const startingPoint = i * cutoffLength;
                detailStrings.push(text.substring(startingPoint, startingPoint + cutoffLength));
            }
        }
        return detailStrings;
    };

    const renderDetail = (text: string, Icon: any) => {
        if (isEmpty(text)) {
            return null;
        }
        return (
            <View style={{ flexDirection: 'row', alignItems: 'center', flex: '1 1 auto' }}>
                <View style={{ width: '9px', marginRight: '8px', paddingTop: '2px' }}>
                    <Icon />
                </View>
                <WrapText
                    text={text}
                    style={{
                        color: '#5C6168',
                        fontFamily: 'Inter',
                        fontSize: '10px',
                        width: '182px',
                        flexWrap: 'wrap',
                    }}
                />
            </View>
        );
    };

    const renderRightHeaderSection = () => (
        <View style={styles.rightHeader}>
            {logo && <Image style={styles.profilePhoto} source={logo} />}
            <View
                style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                <View
                    style={{
                        flexDirection: 'column',
                        gap: '4px',
                    }}>
                    {details.map(({ text, icon }) => renderDetail(text, icon))}
                </View>
            </View>
        </View>
    );

    const renderEducation = () => {
        if (isEmpty(diplomas)) {
            return null;
        }
        const renderItem = (diploma: Diploma) => {
            const {
                title,
                school: { name: schoolName },
                startDate,
                endDate,
                description,
            } = diploma;
            return (
                <View style={{ marginBottom: '16px', width: '146px' }} wrap={false}>
                    <Text style={[styles.regularText, { fontFamily: 'Inter600' }]}>{title}</Text>
                    <Text style={[styles.regularText, { marginTop: '4px' }]}>{schoolName}</Text>
                    <Text style={[styles.regularText, { marginTop: '4px' }]}>{getYearRange(startDate, endDate)}</Text>
                    <Text style={[styles.regularText, { marginTop: '6px' }]}>{description}</Text>
                </View>
            );
        };
        return (
            <View style={{ marginBottom: '9px' }}>
                {renderLeftSectionHeader('FORMATION', '108px')}
                {diplomas.map(renderItem)}
            </View>
        );
    };

    const renderSkills = (skills: string[], title: string, width: string, marginBottom) => {
        if (isEmpty(skills)) {
            return null;
        }
        const renderItem = (skill: string) => (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: '6px' }} wrap={false}>
                <View style={styles.listDot} />
                <View>
                    {getStringsWithLimit(skill, 26).map((skillString, index) => (
                        <Text key={`${skill}-${index}`} style={styles.regularText} orphans={0}>
                            {skillString}
                        </Text>
                    ))}
                </View>
            </View>
        );
        const renderedSkills = skills.slice(0, 10);
        skills.length > 10 && renderedSkills.push('...');
        const style = marginBottom ? { marginBottom: '25px' } : {};
        return (
            <View style={style}>
                {renderLeftSectionHeader(title, width)}
                {renderedSkills.map(renderItem)}
            </View>
        );
    };

    const renderProfileBio = () => (
        <View>
            {renderRightSectionHeader('PROFIL', '64px')}
            <View style={{ width: '320px', marginLeft: '24px', marginBottom: '24px' }}>
                <Text style={styles.bio}>{bio}</Text>
            </View>
        </View>
    );

    const renderBigSection = (list, sectionName, renderItem, width, marginBottom) => {
        if (isEmpty(list)) {
            return null;
        }
        const style = marginBottom ? { marginBottom: '9px' } : {};
        return (
            <View style={style}>
                {renderRightSectionHeader(sectionName, width)}
                {list.map(renderItem)}
            </View>
        );
    };

    const renderExperienceItem = (experience: Experience) => {
        const {
            enterprise: { name: companyName },
            title,
            city,
            startDate,
            endDate,
            description,
        } = experience;
        const renderDescription = () => {
            if (!description) return null;
            return (
                <View>
                    <Text style={[styles.regularText, { marginTop: '6px' }]}>{description}</Text>
                </View>
            );
        };
        return (
            <View style={{ marginLeft: '24px', marginBottom: '16px' }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <View>
                        <Text style={[styles.regularText, { fontFamily: 'Inter600', marginBottom: '3px' }]}>
                            {title}
                        </Text>
                        <Text style={styles.regularText}>{`${companyName}, ${city}`}</Text>
                    </View>
                    <Text style={styles.regularText}>{getYearRange(startDate, endDate)}</Text>
                </View>
                {renderDescription()}
            </View>
        );
    };

    const renderLangueItem = (talentLanguage: TalentLanguage) => {
        const itemLanguage = !isEmpty(talentLanguage?.languages[0].language)
            ? talentLanguage?.languages[0].language[0]
            : null;
        const itemLevel = !isEmpty(talentLanguage?.languages[0].level)
            ? talentLanguage?.languages[0].level[0]
            : null;
        return (
            <View
                style={{ marginLeft: '24px', marginBottom: '16px', flexDirection: 'row', display: 'flex', gap: 5 }}
                wrap={false}>
                <View>
                    <Text style={[styles.certificateName, { fontFamily: 'Inter600', marginBottom: '3px' }]}>
                        {startCase(itemLanguage?.name ?? '-')}
                    </Text>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={styles.regularText}>Niveau : </Text>
                        <Text style={styles.regularText}>{itemLevel?.level ?? '-'}</Text>
                    </View>
                </View>
            </View>
        );
    };

    const renderCertificateItem = (certificate: Certificate) => {
        const {
            startDate,
            endDate,
            title,
            issuer: { name: issuer },
            number,
        } = certificate;
        return (
            <View style={{ marginLeft: '24px', marginBottom: '16px' }} wrap={false}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <View>
                        <Text style={[styles.certificateName, { fontFamily: 'Inter600', marginBottom: '3px' }]}>
                            {title}
                        </Text>
                        <Text style={styles.regularText}>{issuer}</Text>
                    </View>
                    <Text style={styles.regularText}>{getYearRange(startDate, endDate)}</Text>
                </View>
                {number && <Text style={[styles.regularText, { marginTop: '4px' }]}>{`Id: ${number}`}</Text>}
            </View>
        );
    };

    const renderBackground = () => (
        <View style={{ position: 'absolute', top: 0, left: 0 }} fixed>
            <View style={{ flexDirection: 'row', height: '100vh', width: '100vw', flex: 1, margin: 0 }}>
                <View style={{ width: '205px', backgroundColor: '#F7F6FF' }} />
            </View>
        </View>
    );

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <PoweredByUpercut style={styles.poweredByUpercut} fixed />
                {renderBackground()}
                <View style={{ flexDirection: 'row' }}>
                    <View style={styles.leftSection}>
                        {renderNameSection()}
                        {renderEducation()}
                        {renderSkills(hardSkills, 'SAVOIR-FAIRE', '131px', !isEmpty(softSkills))}
                        {renderSkills(softSkills, 'SAVOIR-ÊTRE', '124px', false)}
                    </View>
                    <View style={styles.rightSection}>
                        {renderRightHeaderSection()}
                        {renderProfileBio()}
                        {renderBigSection(
                            experiences,
                            'EXPÉRIENCE',
                            renderExperienceItem,
                            '114px',
                            !isEmpty(certificates),
                        )}
                        {renderBigSection(certificates, 'CERTIFICATION', renderCertificateItem, '143px', false)}
                        {renderBigSection(talentLanguages, 'LANGUE', renderLangueItem, '143px', false)}
                    </View>
                </View>
            </Page>
        </Document>
    );
};

const styles = StyleSheet.create({
    page: {
        padding: '59px 45px 47px 45px',
    },
    bio: {
        color: '#30353D',
        fontFamily: 'Inter',
        fontSize: '10px',
    },
    leftSection: {
        width: '160px',
    },
    nameSection: {
        marginTop: '-20px',
        marginBottom: '70px',
    },
    leftSectionRect: {
        width: '26px',
        height: '1px',
        backgroundColor: '#5C6168',
        marginBottom: '16px',
    },
    rightSectionRect: {
        width: '100px',
        height: '1px',
        backgroundColor: '#5C6168',
        marginBottom: '16px',
    },
    name: {
        color: '#5C6168',
        fontFamily: 'Inter700',
        fontSize: '20px',
        letterSpacing: '2px',
        marginRight: '8px',
    },
    userDetails: {
        color: '#5C6168',
        fontFamily: 'Inter',
        fontSize: '10px',
        width: '182px',
    },
    sectionTitle: {
        color: '#5C6168',
        fontFamily: 'Inter700',
        fontSize: '13px',
        letterSpacing: '3.575px',
        marginBottom: '10px',
    },
    profilePhoto: {
        width: '116px',
        height: '116px',
        objectFit: 'cover',
        borderRadius: '58px',
        marginRight: '22px',
    },
    poweredByUpercut: {
        position: 'absolute',
        top: '19px',
        right: '42.42px',
        width: '86px',
        height: '28px',
    },
    regularText: {
        color: '#5C6168',
        fontFamily: 'Inter',
        fontSize: '10px',
        wordWrap: 'normal',
        textAlign: 'left',
    },
    rightSection: {
        padding: 0,
        flex: 1,
    },
    certificateName: {
        color: '#5C6168',
        fontFamily: 'Inter700',
        fontSize: '10px',
    },
    listDot: {
        width: '4px',
        height: '4px',
        borderRadius: '2px',
        backgroundColor: '#5C6168',
        marginRight: '12px',
    },
    rightHeader: {
        flexDirection: 'row',
        marginLeft: '24px',
        marginTop: '-8px',
        marginBottom: '21px',
    },
});

export default CvTemplateClassic;
